import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import ReactJson from 'react-json-view';
import SplitterLayout from 'react-splitter-layout';
import 'react-splitter-layout/lib/index.css';
import { NetworkManager } from './NetworkManager';
import { CommonUtil } from '../utils/CommonUtil';

export interface JSONCompareViewerRef {
    togglePDF: () => void;
    downloadPDF: () => void;
    toggleFullscreen: () => void;
    isPdfLoading: boolean;
    hasPdf: boolean;
    isFullscreen: boolean;
}

interface JSONCompareViewerProps {
    data: any;
    compareData?: any[]; // Optional array of data to compare against
    onDataChange?: (newData: any) => void;
    title?: string;
    editable?: boolean;
    allowAdd?: boolean;
    allowDelete?: boolean;
    allowCopy?: boolean;
    documentId?: string | number;
    documentType?: string;
    allowFullscreen?: boolean;
    height?: string;
    flaggedComments?: any[]; // Array of comments with paths to highlight
    showExternalControls?: boolean;
    onStateChange?: (state: { isPdfLoading: boolean, hasPdf: boolean, isFullscreen: boolean }) => void;
}

const EMPTY_COMPARE_DATA: any[] = [];

const JSONCompareViewer = forwardRef<JSONCompareViewerRef, JSONCompareViewerProps>(({
    data,
    compareData = EMPTY_COMPARE_DATA,
    onDataChange,
    title = "JSON Viewer",
    editable = true,
    allowAdd = true,
    allowDelete = true,
    allowCopy = true,
    documentId,
    documentType,
    allowFullscreen = true,
    height = "500px",
    flaggedComments = [],
    showExternalControls = false,
    onStateChange
}, ref) => {
    const [currentData, setCurrentData] = useState(data);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const lastStateRef = useRef<string>("");

    useEffect(() => {
        if (onStateChange) {
            const currentState = JSON.stringify({ isPdfLoading, hasPdf: !!pdfUrl, isFullscreen });
            if (currentState !== lastStateRef.current) {
                lastStateRef.current = currentState;
                onStateChange({ isPdfLoading, hasPdf: !!pdfUrl, isFullscreen });
            }
        }
    }, [isPdfLoading, pdfUrl, isFullscreen, onStateChange]);

    useImperativeHandle(ref, () => ({
        togglePDF,
        downloadPDF,
        toggleFullscreen: () => setIsFullscreen(!isFullscreen),
        isPdfLoading,
        hasPdf: !!pdfUrl,
        isFullscreen
    }));

    // Helper to strip highlight flags from data
    const stripFlags = useCallback((obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(item => stripFlags(item));
        const newObj: any = {};
        for (const key in obj) {
            const cleanKey = key.replace(' 🚩', '');
            newObj[cleanKey] = stripFlags(obj[key]);
        }
        return newObj;
    }, []);

    // Create a version of the data with flags in the keys for highlighting
    const flaggedData = useMemo(() => {
        if (!flaggedComments || flaggedComments.length === 0) return currentData;
        
        try {
            let cloned = JSON.parse(JSON.stringify(currentData));
            flaggedComments.forEach(comment => {
                if (!comment.path) return;
                
                const parts = comment.path.split(/[.\[\]]+/).filter(Boolean);
                let target = cloned;
                
                for (let i = 0; i < parts.length - 1; i++) {
                    const part = parts[i];
                    if (target && typeof target === 'object' && part in target) {
                        target = target[part];
                    } else {
                        target = null;
                        break;
                    }
                }
                
                if (target && typeof target === 'object') {
                    const lastKey = parts[parts.length - 1];
                    if (lastKey in target) {
                        const value = target[lastKey];
                        const highlightedKey = `${lastKey} 🚩`;
                        // Reconstruct object to maintain some order if possible
                        const temp: any = {};
                        Object.keys(target).forEach(k => {
                            if (k === lastKey) temp[highlightedKey] = value;
                            else temp[k] = target[k];
                        });
                        // Since we can't easily replace the target reference, 
                        // we just update in place but the reconstruction above 
                        // is just illustrative here unless we replace parent[part]
                        delete target[lastKey];
                        target[highlightedKey] = value;
                    }
                }
            });
            return cloned;
        } catch (e) {
            console.error("Error flagging JSON data:", e);
            return currentData;
        }
    }, [currentData, flaggedComments]);

    useEffect(() => {
        setCurrentData(data);
    }, [data]);

    // Cleanup PDF URL on unmount
    useEffect(() => {
        return () => {
            if (pdfUrl) {
                window.URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    // Automatically load PDF when document changes
    useEffect(() => {
        setPdfUrl(null); // Clear previous PDF when document changes
        
        if (documentId && documentType !== 'payment') {
            const loadOnInit = async () => {
                setIsPdfLoading(true);
                const headers = new Headers();
                const token = localStorage.getItem("token");
                if (token) headers.append("Authorization", `Bearer ${token}`);

                const url = documentType === "statement"
                    ? `api/download/vHubStatement?statementId=${documentId}`
                    : `api/download/vHubInvoice?invoiceId=${documentId}`;

                try {
                    const response = await fetch(`${NetworkManager.serverPath}${url}`, {
                        method: "GET",
                        headers,
                    });
                    if (response.ok) {
                        const blob = await response.blob();
                        if (blob.size > 500) {
                            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                            const blobUrl = window.URL.createObjectURL(pdfBlob);
                            setPdfUrl(blobUrl);
                        }
                    }
                } catch (error) {
                    console.error("Auto PDF load failed:", error);
                } finally {
                    setIsPdfLoading(false);
                }
            };
            loadOnInit();
        }
    }, [documentId, documentType]);

    const downloadPDF = useCallback(async () => {
        if (!documentId) return;

        const orgId = CommonUtil.getSelectedOrgId();
        if (!orgId) return;

        const headers = new Headers({ "Content-Type": "application/json" });
        const token = localStorage.getItem("token");
        if (token) headers.append("Authorization", `Bearer ${token}`);

        const url = documentType === "statement"
            ? `api/download/vHubStatement?statementId=${documentId}`
            : `api/download/vHubInvoice?invoiceId=${documentId}`;

        try {
            const response = await fetch(`${NetworkManager.serverPath}${url}`, {
                method: "GET",
                headers,
            });
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${title || 'document'}.pdf`;
            link.click();
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
            console.error("PDF download failed:", error);
        }
    }, [documentId, documentType, title]);

    const togglePDF = useCallback(async () => {
        if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
            return;
        }

        if (!documentId) return;

        setIsPdfLoading(true);
        const headers = new Headers();
        const token = localStorage.getItem("token");
        if (token) headers.append("Authorization", `Bearer ${token}`);

        const url = documentType === "statement"
            ? `api/download/vHubStatement?statementId=${documentId}`
            : `api/download/vHubInvoice?invoiceId=${documentId}`;

        try {
            const response = await fetch(`${NetworkManager.serverPath}${url}`, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            
            if (blob.size < 500) { // Tiny blobs are usually error messages
                const text = await blob.text();
                if (text.includes("errorMessage") || text.includes("error")) {
                    throw new Error(text);
                }
            }

            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(pdfBlob);
            setPdfUrl(blobUrl);
        } catch (error) {
            console.error("PDF preview failed:", error);
            setPdfUrl(null);
            alert("Unable to load PDF preview. Please check if the document exists.");
        } finally {
            setIsPdfLoading(false);
        }
    }, [documentId, documentType, pdfUrl]);

    // Enhanced diff finder with color coding support
    const findMismatches = (obj1: any, obj2: any, path: string = ''): Array<{path: string, value1: any, value2: any}> => {
        let diffs: Array<{path: string, value1: any, value2: any}> = [];
        const keys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

        keys.forEach(key => {
            const currentPath = path ? `${path}.${key}` : key;
            const val1 = obj1 ? obj1[key] : undefined;
            const val2 = obj2 ? obj2[key] : undefined;

            if (typeof val1 === 'object' && val1 !== null && typeof val2 === 'object' && val2 !== null) {
                diffs = [...diffs, ...findMismatches(val1, val2, currentPath)];
            } else if (val1 !== val2) {
                diffs.push({ path: currentPath, value1: val1, value2: val2 });
            }
        });

        return diffs;
    };

    const mismatches = useMemo(() => {
        if (compareData && compareData.length > 0) {
            return findMismatches(currentData, compareData[0]);
        }
        return [];
    }, [currentData, compareData]);

    const scrollToPath = useCallback((path: string) => {
        if (!path) return;
        const parts = path.split(/[.\[\]]+/).filter(Boolean);
        const lastKey = parts[parts.length - 1];
        const targetText = `${lastKey} 🚩`;

        const keys = document.querySelectorAll('.json-viewer-pane .object-key > span');
        for (const span of Array.from(keys) as HTMLElement[]) {
            if (span.textContent === targetText) {
                span.scrollIntoView({ behavior: 'smooth', block: 'center' });
                
                // Add a temporary "flash" effect to show where we landed
                const row = span.closest('.variable-row') as HTMLElement;
                if (row) {
                    const originalBg = row.style.backgroundColor;
                    const originalTransition = row.style.transition;
                    row.style.transition = 'background-color 0.3s ease';
                    row.style.backgroundColor = 'rgba(255, 193, 7, 0.4)'; // Brighter highlight
                    setTimeout(() => {
                        row.style.backgroundColor = originalBg;
                        setTimeout(() => {
                            row.style.transition = originalTransition;
                        }, 300);
                    }, 1000);
                }
                break;
            }
        }
    }, []);

    // Apply visual highlighting to flagged fields in the DOM
    useEffect(() => {
        const highlightFlagged = () => {
            const keys = document.querySelectorAll('.json-viewer-pane .object-key > span');
            keys.forEach((span: any) => {
                if (span.textContent.includes('🚩')) {
                    // Highlight the key span itself for guaranteed visibility
                    span.style.backgroundColor = '#ddcd9d'; 
                    span.style.color = '#000';
                    span.style.padding = '2px 6px';
                    span.style.borderRadius = '3px';
                    span.style.fontWeight = 'bold';
                    span.style.cursor = 'help';
                    span.title = "Flagged Issue";

                    const row = span.closest('.variable-row');
                    if (row) {
                        row.style.backgroundColor = 'rgba(216, 196, 135, 0.15)'; // Subtle row highlight
                        row.style.borderLeft = '4px solid #ffc107'; // Solid warning border
                        row.style.paddingLeft = '5px';
                    }
                }
            });
        };

        const timeout = setTimeout(highlightFlagged, 150);
        const secondTimeout = setTimeout(highlightFlagged, 500); // Second attempt for slower renders

        const observer = new MutationObserver(() => {
            highlightFlagged();
        });

        // Observe the entire component container to catch panes being added
        const container = document.querySelector('.json-compare-viewer');
        if (container) {
            observer.observe(container, { childList: true, subtree: true });
        }

        return () => {
            clearTimeout(timeout);
            clearTimeout(secondTimeout);
            observer.disconnect();
        };
    }, [flaggedData, isFullscreen, pdfUrl]); // Re-run when layout changes

    // Restrict input for number fields to only allow numeric characters
    useEffect(() => {
        const restrictToNumbers = (input: HTMLInputElement) => {
            if (input.dataset.numberRestricted) return;
            
            // Check if it's likely a number field (initial value is numeric)
            const initialValue = input.value;
            const isNumeric = !isNaN(initialValue as any) && initialValue.trim() !== '';
            
            if (isNumeric) {
                input.dataset.numberRestricted = 'true';
                input.addEventListener('keydown', (e: KeyboardEvent) => {
                    // Allow: control keys (Backspace, Delete, arrows, etc.)
                    if (e.key.length > 1 || e.ctrlKey || e.metaKey) return;
                    
                    // Allow: digits 0-9
                    if (/^[0-9]$/.test(e.key)) return;
                    
                    // Allow: decimal point (only one)
                    if (e.key === '.' && !input.value.includes('.')) return;
                    
                    // Allow: minus sign (only at the beginning and only one)
                    if (e.key === '-' && input.selectionStart === 0 && !input.value.includes('-')) return;
                    
                    // Block everything else
                    e.preventDefault();
                });

                // Also handle paste to ensure only numeric data is pasted
                input.addEventListener('paste', (e: ClipboardEvent) => {
                    const pasteData = e.clipboardData?.getData('text');
                    if (pasteData && isNaN(Number(pasteData))) {
                        e.preventDefault();
                    }
                });
            }
        };

        const observer = new MutationObserver((mutations) => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node instanceof HTMLElement) {
                        if (node.tagName === 'INPUT' && node.classList.contains('variable-editor')) {
                            restrictToNumbers(node as HTMLInputElement);
                        } else {
                            const inputs = node.querySelectorAll('.variable-editor');
                            inputs.forEach(i => restrictToNumbers(i as HTMLInputElement));
                        }
                    }
                });
            });
        });

        const container = document.querySelector('.json-compare-viewer');
        if (container) {
            observer.observe(container, { childList: true, subtree: true });
            // Check existing inputs
            container.querySelectorAll<HTMLInputElement>('.variable-editor').forEach(restrictToNumbers);
        }

        return () => observer.disconnect();
    }, [isFullscreen, pdfUrl]); // Re-run if container might have been recreated

    const handleEdit = (edit: any) => {
        const { updated_src, namespace, name, new_value, existing_value } = edit;

        // Type preservation: if original was a number/boolean, try to keep it that way
        if (typeof existing_value === 'number') {
            const num = Number(new_value);
            if (isNaN(num) || String(new_value).trim() === '') {
                return false; // Prevent invalid number edit
            }
            
            // Ensure it's stored as number in updated_src
            let target = updated_src;
            for (const key of namespace) target = target[key];
            target[name] = num;
        } else if (typeof existing_value === 'boolean' && typeof new_value === 'string') {
            const lowVal = new_value.toLowerCase().trim();
            if (lowVal === 'true' || lowVal === 'false') {
                let target = updated_src;
                for (const key of namespace) target = target[key];
                target[name] = (lowVal === 'true');
            } else {
                return false; // Prevent invalid boolean edit
            }
        }

        const cleanData = stripFlags(updated_src);
        setCurrentData(cleanData);
        if (onDataChange) {
            onDataChange(cleanData);
        }
        return true;
    };

    const handleAdd = (add: any) => {
        const { updated_src, namespace, name, new_value } = add;

        // Auto-cast strings that look like numbers or booleans for new fields
        if (typeof new_value === 'string') {
            const num = Number(new_value);
            // Only auto-cast if it's a clear number and not leading with zero (unless it's '0' or '0.')
            if (!isNaN(num) && new_value.trim() !== '' && (!new_value.startsWith('0') || new_value === '0' || new_value.startsWith('0.'))) {
                let target = updated_src;
                for (const key of namespace) target = target[key];
                target[name] = num;
            } else {
                const lowVal = new_value.toLowerCase().trim();
                if (lowVal === 'true' || lowVal === 'false') {
                    let target = updated_src;
                    for (const key of namespace) target = target[key];
                    target[name] = (lowVal === 'true');
                }
            }
        }

        const cleanData = stripFlags(updated_src);
        setCurrentData(cleanData);
        if (onDataChange) {
            onDataChange(cleanData);
        }
        return true;
    };

    const handleDelete = (del: any) => {
        const cleanData = stripFlags(del.updated_src);
        setCurrentData(cleanData);
        if (onDataChange) {
            onDataChange(cleanData);
        }
    };


    const renderViewer = (src: any, name: string, isEditable: boolean) => (
        <div className="json-viewer-pane position-relative" style={{ height: '100%', overflow: 'auto' }}>
            <button 
                className="btn btn-sm btn-outline-secondary position-absolute top-0 end-0 m-3 z-index-1"
                title="Copy Full JSON"
                style={{ zIndex: 10, opacity: 0.7 }}
                onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(src, null, 2));
                    // Briefly change icon to show success
                    const icon = document.getElementById(`copy-icon-${name}`);
                    if (icon) {
                        icon.classList.remove('fa-copy');
                        icon.classList.add('fa-check', 'text-success');
                        setTimeout(() => {
                            icon.classList.remove('fa-check', 'text-success');
                            icon.classList.add('fa-copy');
                        }, 2000);
                    }
                }}
            >
                <i id={`copy-icon-${name}`} className="fa-solid fa-copy"></i>
            </button>
            <ReactJson
                src={isEditable && src === currentData ? flaggedData : src}
                theme={"solarized"}
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={allowCopy}
                onEdit={isEditable ? handleEdit : false}
                onAdd={isEditable && allowAdd ? handleAdd : false}
                onDelete={isEditable && allowDelete ? handleDelete : false}
                name={false}
                style={{ borderRadius: '4px', padding: '5px', backgroundColor: '#0000', fontSize: '14px' }}
                collapsed={false}
                indentWidth={2}
                quotesOnKeys={false}
            />
        </div>
    );

    return (
        <div className={`json-compare-viewer ${isFullscreen ? 'fullscreen-viewer' : ''}`} 
            style={isFullscreen ? {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 9999,
                backgroundColor: 'white',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column'
            } : { 
                height: height, 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
            }}>
            <style>
                {`
                    .json-compare-viewer .splitter-layout {
                        position: relative;
                        flex: 1;
                        height: 100%;
                        width: 100%;
                    }
                    .json-compare-viewer .splitter-layout .layout-pane {
                        overflow: auto;
                        height: 100%;
                    }
                    .issue-item-hover:hover {
                        background-color: rgba(220, 53, 69, 0.1);
                        text-decoration: underline;
                    }
                `}
            </style>
            
            {isFullscreen && (
                <div className="d-flex justify-content-between align-items-center p-2 bg-light border-bottom">
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold">{`${title} (Fullscreen)`}</span>
                        {!showExternalControls && documentId && documentType !== 'payment' && (
                            <>
                                {isPdfLoading ? (
                                <i className="fa-solid fa-spinner fa-spin text-primary ms-1"></i>
                            ) : (
                                <i 
                                    className={`fa-solid ${pdfUrl ? 'fa-file-pdf text-danger' : 'fa-file-lines text-secondary'} cursor-pointer ms-1`} 
                                    style={{ cursor: 'pointer' }}
                                    onClick={togglePDF}
                                    title={pdfUrl ? "Hide PDF Preview" : "Show PDF Preview"}
                                ></i>
                            )}
                            <i 
                                className="fa-solid fa-download text-primary cursor-pointer ms-2" 
                                style={{ cursor: 'pointer' }}
                                onClick={downloadPDF}
                                title="Download PDF"
                            ></i>
                        </>
                    )}
                </div>
                <div className="d-flex align-items-center gap-2">
                    {showExternalControls && isFullscreen && (
                        <button 
                            className="btn btn-sm btn-outline-secondary me-2" 
                            onClick={() => setIsFullscreen(false)}
                            title="Close Fullscreen"
                        >
                            <i className="fa-solid fa-compress me-1"></i>
                        </button>
                    )}
                    {compareData.length > 0 && (
                        <span className="badge bg-info text-dark me-2">
                            {mismatches.length} differences found
                        </span>
                    )}
                    {!showExternalControls && allowFullscreen && (
                        <button 
                            className="btn btn-sm btn-outline-secondary" 
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            title={isFullscreen ? "Exit Fullscreen" : "Expand Outside"}
                        >
                            <i className={`fa-solid ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
                        </button>
                    )}
                </div>
            </div>)}

            {flaggedComments.length > 0 && (
                <div className="alert alert-danger p-2 mb-0" style={{ fontSize: '12px' }}>
                    <div className="d-flex justify-content-start align-items-center">
                        <strong>{flaggedComments.length} Flagged Issues 🚩:</strong>
                        <small className="text-muted ms-2">(Click an issue from the list to locate in JSON)</small>
                    </div>
                    <ul className="mb-0 mt-1" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                        {flaggedComments.map((m, i) => (
                            <li key={i} 
                                onClick={() => scrollToPath(m.path)}
                                style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '4px' }}
                                className="issue-item-hover"
                            >
                                <span className="fw-bold text-danger">{m.field}:</span> {m.message} 
                                {m.expected && <span className="ms-2">(Expected: {m.expected}, Actual: {m.actual})</span>}
                                <span className="text-muted ms-2">at <code>{m.path}</code></span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {mismatches.length > 0 && (
                <div className="alert alert-warning p-2 mb-0" style={{ fontSize: '12px' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <strong>Differences found ({mismatches.length}):</strong>
                        <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                                // Scroll to first difference (simplified)
                                const firstDiff = document.querySelector('.json-viewer-pane');
                                if (firstDiff) {
                                    firstDiff.scrollTop = 0;
                                }
                            }}
                        >
                            Jump to first
                        </button>
                    </div>
                    <ul className="mb-0 mt-1" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                        {mismatches.slice(0, 10).map((m, i) => (
                            <li key={i}>
                                <span className="text-danger">Old:</span> {m.value2} 
                                <span className="text-success ms-2">New:</span> {m.value1}
                                <span className="text-muted ms-2">at <code>{m.path}</code></span>
                            </li>
                        ))}
                        {mismatches.length > 10 && <li>... and {mismatches.length - 10} more</li>}
                    </ul>
                </div>
            )}
            
            <div style={{ flex: 1, position: 'relative', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {pdfUrl ? (
                    <SplitterLayout primaryIndex={0} percentage={true} secondaryInitialSize={60}>
                        <div style={{ height: '100%', overflowY: 'hidden', position: 'relative' }}>
                            {compareData.length > 0 ? (
                                <SplitterLayout primaryIndex={0} percentage={true} secondaryInitialSize={70}>
                                    {renderViewer(currentData, "New / Edited", editable)}
                                    {renderViewer(compareData[0], "Original", false)}
                                </SplitterLayout>
                            ) : (
                                renderViewer(currentData, title, editable)
                            )}
                        </div>
                        <div className="pdf-viewer-container" style={{ height: '100%', overflowY: 'hidden', backgroundColor: '#525659' }}>
                            <embed 
                                key={pdfUrl}
                                src={`${pdfUrl}#toolbar=1&navpanes=0&scrollbar=1&view=Fit&zoom=53`} 
                                type="application/pdf"
                                width="100%" 
                                height="100%" 
                                style={{ border: 'none', padding: 1, margin: 0 }} 
                            />
                        </div>
                    </SplitterLayout>
                ) : (
                    compareData.length > 0 ? (
                        <SplitterLayout primaryIndex={0} percentage={true} secondaryInitialSize={70}>
                            {renderViewer(currentData, "New / Edited", editable)}
                            {renderViewer(compareData[0], "Original", false)}
                        </SplitterLayout>
                    ) : (
                        renderViewer(currentData, title, editable)
                    )
                )}
            </div>
        </div>
    );
});

export default JSONCompareViewer;